import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  KeyRound,
  Bell,
  BellOff,
  Utensils,
  Star,
  DollarSign,
  Building2,
  CalendarDays,
  Save,
  ShieldCheck,
  Fingerprint,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  registerBiometric,
  removeBiometric,
  hasBiometricEnrolled,
  isBiometricAvailable,
  isBiometricSupported,
  syncBiometricEnrollment,
} from '../utils/biometric';
import './UserProfile.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

export default function UserProfile({ authToken, userEmail, onBack, onProfileUpdate }) {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // Biometric state — recomputed on every render for the current device.
  const bioSupported = isBiometricSupported();
  const bioAvailable = isBiometricAvailable();
  const [bioEnrolled, setBioEnrolled] = useState(
    bioAvailable && hasBiometricEnrolled(userEmail)
  );
  const [bioBusy, setBioBusy] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const flash = (type, text, ms = 3000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), ms);
  };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/me/profile`, { headers }),
        axios.get(`${API_URL}/api/users/me/stats`, { headers }),
      ]);
      setProfile(pRes.data);
      setStats(sRes.data);
      setDisplayName(pRes.data.display_name || '');
      setEmail(pRes.data.email || '');
      setEmailOptIn(pRes.data.email_opt_in !== false);
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [authToken]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Sync biometric enrollment from backend so the button reflects reality
  // even if this browser's localStorage was cleared (iPad Safari ITP / PWA).
  useEffect(() => {
    if (!userEmail || !bioAvailable) return;
    let cancelled = false;
    syncBiometricEnrollment(userEmail).then((res) => {
      if (cancelled) return;
      setBioEnrolled(!!res.enrolled);
    });
    return () => { cancelled = true; };
  }, [userEmail, bioAvailable]);

  const saveDisplayName = async () => {
    if (!displayName.trim()) return flash('error', 'Display name is required');
    try {
      setSaving(true);
      const res = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        { new_display_name: displayName.trim() },
        { headers }
      );
      const newName = res.data.display_name || displayName.trim();
      flash('success', 'Display name updated');
      localStorage.setItem('display_name', newName);
      sessionStorage.setItem('display_name', newName);
      setProfile((p) => ({ ...p, display_name: newName }));
      onProfileUpdate && onProfileUpdate({ display_name: newName });
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update display name');
    } finally {
      setSaving(false);
    }
  };

  const saveEmail = async () => {
    if (!email.trim() || !email.includes('@')) return flash('error', 'Enter a valid email');
    if (email.trim().toLowerCase() === userEmail.toLowerCase())
      return flash('error', 'That is already your current email');
    try {
      setSaving(true);
      const res = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        { new_email: email.trim() },
        { headers }
      );
      const newEmail = res.data.email || email.trim();
      flash('success', 'Email updated successfully');
      localStorage.setItem('user_email', newEmail);
      sessionStorage.setItem('user_email', newEmail);
      setProfile((p) => ({ ...p, email: newEmail }));
      onProfileUpdate && onProfileUpdate({ email: newEmail });
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  const toggleOptIn = async () => {
    const next = !emailOptIn;
    try {
      setSaving(true);
      const res = await axios.put(
        `${API_URL}/api/users/me/email-opt-in`,
        { email_opt_in: next },
        { headers }
      );
      setEmailOptIn(res.data.email_opt_in);
      flash('success', `Emails ${res.data.email_opt_in ? 'enabled' : 'disabled'}`);
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update email preferences');
    } finally {
      setSaving(false);
    }
  };

  const changePin = async () => {
    if (newPin.length < 4 || newPin.length > 12) return flash('error', 'PIN must be 4–12 characters');
    if (newPin !== confirmPin) return flash('error', 'New PIN and confirmation do not match');
    try {
      setSaving(true);
      await axios.post(
        `${API_URL}/api/auth/change-pin-simple`,
        { new_pin: newPin, confirm_pin: confirmPin },
        { headers }
      );
      setNewPin('');
      setConfirmPin('');
      flash('success', 'PIN changed successfully');
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to change PIN');
    } finally {
      setSaving(false);
    }
  };

  const enableBiometric = async () => {
    setBioBusy(true);
    try {
      const result = await registerBiometric(userEmail, authToken);
      if (result.success) {
        setBioEnrolled(true);
        flash('success', 'Fingerprint / Face ID enabled on this device!', 4000);
      } else {
        flash('error', 'Could not enable biometric: ' + (result.error || 'unknown error'), 6000);
      }
    } finally {
      setBioBusy(false);
    }
  };

  const resetBiometric = async () => {
    if (!window.confirm('Remove fingerprint login from this browser? You can re-enable it right after.')) return;
    setBioBusy(true);
    try {
      await removeBiometric(userEmail, authToken);
      setBioEnrolled(false);
      flash('success', 'Fingerprint reset. Tap "Enable" to re-enroll.', 4000);
    } finally {
      setBioBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">Loading your profile…</div>
      </div>
    );
  }

  return (
    <div className="profile-page" data-testid="user-profile-page">
      <div className="profile-container">
        <button
          className="profile-back-btn"
          onClick={onBack}
          data-testid="profile-back-btn"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        <header className="profile-header">
          <div className="profile-avatar">
            <UserIcon size={36} />
          </div>
          <div>
            <h1 className="profile-title">{displayName || 'Your Profile'}</h1>
            <p className="profile-subtitle">{profile?.email}</p>
            {profile?.is_admin && (
              <span className="profile-admin-badge">
                <ShieldCheck size={12} /> Admin
              </span>
            )}
          </div>
        </header>

        {msg.text && (
          <div
            className={`profile-flash profile-flash-${msg.type}`}
            data-testid="profile-flash"
          >
            {msg.text}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <section className="profile-stats" data-testid="profile-stats">
            <h2 className="profile-section-title">Your Dining Stats</h2>
            <div className="profile-stats-grid">
              <div className="profile-stat-card profile-stat-amber">
                <Utensils size={20} />
                <div className="profile-stat-value">{stats.visit_count}</div>
                <div className="profile-stat-label">Total Visits</div>
              </div>
              <div className="profile-stat-card profile-stat-emerald">
                <Building2 size={20} />
                <div className="profile-stat-value">{stats.unique_restaurants_visited}</div>
                <div className="profile-stat-label">Restaurants Tried</div>
              </div>
              <div className="profile-stat-card profile-stat-violet">
                <DollarSign size={20} />
                <div className="profile-stat-value">{fmtMoney(stats.total_spent)}</div>
                <div className="profile-stat-label">Total Spent</div>
              </div>
              <div className="profile-stat-card profile-stat-rose">
                <Star size={20} />
                <div className="profile-stat-value">
                  {stats.average_rating != null ? stats.average_rating.toFixed(1) : '—'}
                </div>
                <div className="profile-stat-label">Avg. Rating</div>
              </div>
              <div className="profile-stat-card profile-stat-sky">
                <Utensils size={20} />
                <div className="profile-stat-value">{stats.top_cuisine || '—'}</div>
                <div className="profile-stat-label">Top Cuisine</div>
              </div>
              <div className="profile-stat-card profile-stat-slate">
                <CalendarDays size={20} />
                <div className="profile-stat-value">{stats.last_visit_date || '—'}</div>
                <div className="profile-stat-label">Last Visit</div>
              </div>
            </div>
          </section>
        )}

        {/* Account */}
        <section className="profile-section">
          <h2 className="profile-section-title">Account</h2>

          <div className="profile-field">
            <Label htmlFor="profile-display-name">
              <UserIcon size={14} /> Display Name
            </Label>
            <div className="profile-row">
              <Input
                id="profile-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                data-testid="profile-display-name-input"
              />
              <Button
                onClick={saveDisplayName}
                disabled={saving || displayName === profile?.display_name}
                data-testid="profile-save-display-name-btn"
              >
                <Save size={14} /> Save
              </Button>
            </div>
          </div>

          <div className="profile-field">
            <Label htmlFor="profile-email">
              <Mail size={14} /> Email Address
            </Label>
            <div className="profile-row">
              <Input
                id="profile-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="profile-email-input"
              />
              <Button
                onClick={saveEmail}
                disabled={saving || email === profile?.email}
                data-testid="profile-save-email-btn"
              >
                <Save size={14} /> Save
              </Button>
            </div>
          </div>

          <div className="profile-field profile-toggle-row">
            <div>
              <Label>
                {emailOptIn ? <Bell size={14} /> : <BellOff size={14} />} Email Notifications
              </Label>
              <p className="profile-hint">
                Receive the weekly digest and other broadcasts. You can opt out anytime.
              </p>
            </div>
            <button
              className={`profile-toggle ${emailOptIn ? 'on' : 'off'}`}
              onClick={toggleOptIn}
              disabled={saving}
              data-testid="profile-email-optin-toggle"
              role="switch"
              aria-checked={emailOptIn}
              aria-pressed={emailOptIn}
            >
              <span className="profile-toggle-knob" />
            </button>
          </div>
        </section>

        {/* Security */}
        <section className="profile-section">
          <h2 className="profile-section-title">Security</h2>
          <div className="profile-field">
            <Label htmlFor="profile-new-pin">
              <KeyRound size={14} /> New PIN (4–12 characters)
            </Label>
            <Input
              id="profile-new-pin"
              type="password"
              value={newPin}
              maxLength={12}
              onChange={(e) => setNewPin(e.target.value.slice(0, 12))}
              placeholder="Enter new PIN"
              data-testid="profile-new-pin-input"
            />
          </div>
          <div className="profile-field">
            <Label htmlFor="profile-confirm-pin">
              <KeyRound size={14} /> Confirm New PIN
            </Label>
            <Input
              id="profile-confirm-pin"
              type="password"
              value={confirmPin}
              maxLength={12}
              onChange={(e) => setConfirmPin(e.target.value.slice(0, 12))}
              placeholder="Re-enter new PIN"
              data-testid="profile-confirm-pin-input"
            />
          </div>
          <Button
            onClick={changePin}
            disabled={saving || !newPin || !confirmPin}
            className="profile-pin-btn"
            data-testid="profile-change-pin-btn"
          >
            <KeyRound size={14} /> Change PIN
          </Button>
          <p className="profile-hint" style={{ marginTop: '0.5rem' }}>
            Changing your PIN will disable biometric login until you re-enroll.
          </p>

          {/* Biometric enrollment — visible on all devices with a WebAuthn
              platform authenticator (iPhone, iPad, Android, Touch ID, Face ID,
              Windows Hello). Falls back to a helpful message otherwise. */}
          <div
            className="profile-field"
            style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}
            data-testid="profile-biometric-section"
          >
            <Label>
              <Fingerprint size={14} /> Fingerprint / Face ID Login
            </Label>
            {!bioSupported && (
              <p className="profile-hint" data-testid="profile-biometric-unsupported">
                This browser doesn't support WebAuthn biometric login.
              </p>
            )}
            {bioSupported && !bioAvailable && (
              <p className="profile-hint" data-testid="profile-biometric-unavailable">
                Biometric login is enabled on phones and tablets. Open this page on your
                iPad or phone to set it up there.
              </p>
            )}
            {bioAvailable && (
              <>
                <p className="profile-hint" style={{ marginBottom: '0.75rem' }}>
                  {bioEnrolled
                    ? 'Biometric login is enabled on this browser. Reset it here to re-enroll.'
                    : 'Enable Fingerprint / Face ID for faster login on this device.'}
                </p>
                {!bioEnrolled ? (
                  <Button
                    onClick={enableBiometric}
                    disabled={bioBusy}
                    className="profile-pin-btn"
                    data-testid="enroll-biometric-btn"
                    style={{ backgroundColor: '#10b981', color: 'white' }}
                  >
                    <Fingerprint size={14} /> {bioBusy ? 'Prompting device…' : 'Enable Fingerprint / Face ID'}
                  </Button>
                ) : (
                  <Button
                    onClick={resetBiometric}
                    disabled={bioBusy}
                    className="profile-pin-btn"
                    data-testid="reset-biometric-btn"
                    style={{ backgroundColor: '#f59e0b', color: 'white' }}
                  >
                    <Fingerprint size={14} /> Reset Fingerprint
                  </Button>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
